import React, { useEffect, useMemo, useState } from 'react';
import { Gauge } from 'lucide-react';
import { FormSection, FormStack, GatewayDrawer, SearchableSelect, fieldClass, ghostBtn, labelClass, primaryBtn, selectField } from '../components/GatewayUi';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaAssignmentStatus, QuotaListingTab, QuotaScope } from '../governedMcpTypes';
import { currentQuotaMonth, findQuotaAssignment, formatQuotaMonth, isQuotaMonth } from '../quotaUsage';

const SCOPE_LABEL: Record<QuotaListingTab, string> = {
  department: 'Department',
  role: 'Role',
  user: 'User',
};

export default function QuotaAssignDrawer({
  open,
  listing,
  clientId,
  month,
  isLight,
  onClose,
  onToast,
}: {
  open: boolean;
  listing: QuotaListingTab;
  clientId: string;
  month: string;
  isLight: boolean;
  onClose: () => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, quotaDirectory, setQuotaAssignment } = useGovernedMcp();
  const [formClientId, setFormClientId] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [formMonth, setFormMonth] = useState(currentQuotaMonth());
  const [tokenLimit, setTokenLimit] = useState('');
  const [status, setStatus] = useState<QuotaAssignmentStatus>('active');
  const [error, setError] = useState<string | null>(null);

  const keyUsers = useMemo(() => {
    const seen = new Set<string>();
    return state.apiKeys
      .map((key) => {
        const id = key.userId.trim();
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const dirUser = quotaDirectory.users.find((item) => item.id === id);
        return {
          id,
          name: dirUser?.name || key.name || key.username || id,
          hint: dirUser?.email || key.email || '',
        };
      })
      .filter((item): item is { id: string; name: string; hint: string } => Boolean(item));
  }, [quotaDirectory.users, state.apiKeys]);

  const options =
    listing === 'department'
      ? quotaDirectory.departments.map((item) => ({ id: item.id, label: item.name }))
      : listing === 'role'
        ? quotaDirectory.roles.map((item) => ({ id: item.id, label: item.name }))
        : keyUsers.map((item) => ({ id: item.id, label: item.name, hint: item.hint }));

  useEffect(() => {
    if (!open) return;
    const nextClient = clientId !== 'all' ? clientId : state.clients[0]?.id || '';
    const nextMonth = isQuotaMonth(month) ? month : currentQuotaMonth();
    setFormClientId(nextClient);
    setFormMonth(nextMonth);
    setScopeId('');
    setTokenLimit('');
    setStatus('active');
    setError(null);
  }, [clientId, month, open, state.clients]);

  const selected = options.find((item) => item.id === scopeId);
  const existing =
    formClientId && scopeId && isQuotaMonth(formMonth)
      ? findQuotaAssignment(state.quotaAssignments, listing as QuotaScope, scopeId, formClientId, formMonth)
      : undefined;

  useEffect(() => {
    if (!open) return;
    setTokenLimit(existing ? String(existing.tokenLimit) : '');
    setStatus(existing?.status === 'inactive' ? 'inactive' : 'active');
  }, [existing, open, formClientId, formMonth, scopeId]);

  const submit = () => {
    const parsed = Number(tokenLimit.replace(/,/g, '').trim());
    const result = setQuotaAssignment(
      listing as QuotaScope,
      scopeId,
      formClientId,
      parsed,
      selected?.label,
      formMonth,
      status
    );
    if (!result.ok) {
      setError(result.reason);
      onToast('error', result.reason);
      return;
    }
    onToast('success', `Quota assigned for ${selected?.label || SCOPE_LABEL[listing]} · ${formatQuotaMonth(formMonth)}.`);
    onClose();
  };

  return (
    <GatewayDrawer
      open={open}
      title="Assign quota"
      subtitle={`${SCOPE_LABEL[listing]} · monthly token limit`}
      eyebrow="Governed MCP / Quotas & Spendings"
      icon={<Gauge size={18} />}
      isLight={isLight}
      onClose={onClose}
      size="lg"
      maximizable
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            Assign quota
          </button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <FormSection title="Assignment" description="Each matching user receives this limit on the selected client for this month." isLight={isLight}>
          <div className="space-y-4">
            <div>
              <label className={labelClass(isLight)}>Client</label>
              <select className={`${selectField(isLight)} w-full`} value={formClientId} onChange={(event) => setFormClientId(event.target.value)}>
                <option value="">Select a client</option>
                {state.clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            <SearchableSelect
              label={SCOPE_LABEL[listing]}
              value={scopeId}
              options={options}
              onChange={setScopeId}
              isLight={isLight}
              placeholder={`Select a ${SCOPE_LABEL[listing].toLowerCase()}`}
              searchPlaceholder={listing === 'user' ? 'Search name or email…' : `Search ${SCOPE_LABEL[listing].toLowerCase()}s…`}
            />
            <div>
              <label className={labelClass(isLight)}>Month</label>
              <input
                type="month"
                className={fieldClass(isLight)}
                value={formMonth}
                onChange={(event) => setFormMonth(event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass(isLight)}>Monthly token limit</label>
              <input
                className={`${fieldClass(isLight)} tabular-nums`}
                inputMode="numeric"
                placeholder="5000000"
                value={tokenLimit}
                onChange={(event) => setTokenLimit(event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass(isLight)}>Status</label>
              <select
                className={`${selectField(isLight)} w-full`}
                value={status}
                onChange={(event) => setStatus(event.target.value as QuotaAssignmentStatus)}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </FormSection>
      </FormStack>
    </GatewayDrawer>
  );
}
